import { memo } from 'react'

import { NextLink } from '../../../../app/_components/navigation/NextLink'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { NostoProductRecommendationsSection } from '../../../../brunswick/components/NostoProductRecommendationsSection'
import { Collection } from '../../../../brunswick/components/pages/Collection/Collection'
import { ErrorViewHead, ErrorViewSection } from '../../../../brunswick/components/pages/ErrorView'
import { NavContainer } from '../../../../brunswick/containers/NavContainer'
import { SeoMeta } from '../../../../components/SeoMeta'
import { canonicalBaseUrls, imageSrcs, relativePaths } from '../../../../constants/figs'
import { SearchOverlayForm } from '../Search/SearchOverlayForm'

import { SEARCH_REGEX } from './SearchPage'
import {
  AutoCompleteOverlay,
  AutoCompleteOverlayBottom,
  AutoCompleteOverlayTop,
  Body,
  GridWrapper,
  HiddenH1,
  NoResultsText,
  RelatedSearchTerm,
  RelatedSearches,
  ResultsCount,
  SearchOverlayWrapper,
} from './styles'
import { useViewModel } from './useViewModel'

type Props = ReturnType<typeof useViewModel>

export const Template = memo<Props>(
  ({
    totalItemsCount,
    autoCompleteOverlayVisible,
    filters,
    hasMoreItems,
    visualFiltersSection,
    visualFiltersSectionV2,
    hideAutoCompleteOverlay,
    initialSearchText,
    loadMoreItems,
    status,
    fetchingMore,
    numberOfResultsText,
    onAutoComplete,
    pageTitle,
    searchVariantGroups,
    relatedSearches,
    relatedSearchesTitle,
    searchOverlayFormRef,
    statusCode,
    submitForm,
    substituteSearchTerm,
  }) => {
    const isLoading = status === 'pending' && !fetchingMore
    const { promoBarHeightDesktopPixels, promoBarHeightMobilePixels } = NavContainer.useContainer()
    const {
      pages: {
        pageGoneError: { image, links, subheading, heading },
      },
    } = useTranslation()

    if (statusCode === 410 || SEARCH_REGEX.exec(initialSearchText) === null) {
      return (
        <>
          <SearchOverlayWrapper>
            <SearchOverlayForm
              initialSearchText={initialSearchText}
              onAutoComplete={onAutoComplete}
              onClearFormClick={hideAutoCompleteOverlay}
              onFormSubmit={submitForm}
              ref={searchOverlayFormRef}
              resultsInFrontOfBackdrop
              shouldDisplaySearchOverlay={true}
            />
          </SearchOverlayWrapper>
          <ErrorViewHead canonicalUrl={`${canonicalBaseUrls.home}${relativePaths.page410Path}`} />
          <ErrorViewSection
            heading={heading}
            subheading={subheading}
            imageAlt={image.alt}
            imageSrc={imageSrcs.pageGoneError}
            links={[
              { ...links.shopWomen, href: relativePaths.shopWomensBestSellersPath },
              { ...links.shopMen, href: relativePaths.shopMensBestSellersPath },
            ]}
          />
        </>
      )
    }

    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const renderV2VisualFilters = visualFiltersSectionV2?.id
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const renderV1VisualFilters = !renderV2VisualFilters && visualFiltersSection?.id

    return (
      <>
        <SeoMeta title={pageTitle} />

        <Body>
          <AutoCompleteOverlay show={autoCompleteOverlayVisible}>
            <AutoCompleteOverlayTop
              show={autoCompleteOverlayVisible}
              promoBarHeightDesktopPixels={promoBarHeightDesktopPixels}
              promoBarHeightMobilePixels={promoBarHeightMobilePixels}
            />
            <AutoCompleteOverlayBottom
              onClick={hideAutoCompleteOverlay}
              show={autoCompleteOverlayVisible}
            />
          </AutoCompleteOverlay>
          <SearchOverlayWrapper>
            <SearchOverlayForm
              initialSearchText={initialSearchText}
              onAutoComplete={onAutoComplete}
              onClearFormClick={hideAutoCompleteOverlay}
              onFormSubmit={submitForm}
              ref={searchOverlayFormRef}
              resultsInFrontOfBackdrop
              shouldDisplaySearchOverlay={true}
            />
          </SearchOverlayWrapper>
          <HiddenH1>{`${initialSearchText} ${numberOfResultsText} Search Results`}</HiddenH1>
          <ResultsCount role='alert' aria-live='polite'>
            {numberOfResultsText}
          </ResultsCount>
          {relatedSearches.length > 0 && (
            <RelatedSearches>
              <span>{relatedSearchesTitle}: </span>
              {relatedSearches.map((searchTerm, index) => (
                <RelatedSearchTerm key={index}>
                  <NextLink href={`/search?q=${searchTerm}`}>{searchTerm}</NextLink>
                </RelatedSearchTerm>
              ))}
            </RelatedSearches>
          )}
          {!isLoading && searchVariantGroups.length === 0 && (
            <>
              <NoResultsText>No results for '{initialSearchText}'</NoResultsText>
              <NostoProductRecommendationsSection
                fadeIn
                nostoPlacementId='searchpage-nosto-1'
                title={substituteSearchTerm}
                gutterSize='expanded'
              />

              <NostoProductRecommendationsSection
                fadeIn
                nostoPlacementId='searchpage-nosto-2'
                gutterSize='expanded'
              />
            </>
          )}
          {isLoading || searchVariantGroups.length > 0 ? (
            <GridWrapper>
              <Collection.Wrapper
                collectionHandle='search'
                totalItemsCount={totalItemsCount}
                filters={filters}
                filterConfig={null}
              >
                {renderV2VisualFilters ? (
                  <Collection.VisualFilters {...visualFiltersSectionV2} />
                ) : null}

                {renderV1VisualFilters ? (
                  <Collection.VisualFilters {...visualFiltersSection} />
                ) : null}

                <Collection.Controls isLoading={isLoading} />

                <Collection.Grid isLoading={isLoading}>
                  {searchVariantGroups.map((variantGroup, index) => (
                    <Collection.GridItem
                      key={variantGroup.id}
                      index={index}
                      item={{
                        ...variantGroup,
                        collectionScope: undefined,
                      }}
                    />
                  ))}
                </Collection.Grid>

                <Collection.Footer
                  loadMore={hasMoreItems && !isLoading ? loadMoreItems : undefined}
                />
              </Collection.Wrapper>
            </GridWrapper>
          ) : null}
        </Body>
      </>
    )
  }
)
